import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGitHubConnection, type SyncStatus } from '@/hooks/useGitHubConnection';
import { InstallGitHubApp } from '@/components/github/InstallGitHubApp';
import { ConnectionStatus } from '@/components/github/ConnectionStatus';
import { PlanBadge } from '@/components/github/PlanBadge';
import { RepoLimitModal } from '@/components/github/RepoLimitModal';
import { TagChips } from '@/components/settings/TagChips';
import { RepoTagEditor } from '@/components/settings/RepoTagEditor';
import { projectsApi, type TaxonomyDimension } from '@/services/projectsApi';
import { githubApi, RepoLimitError, type Repo } from '@/services/githubApi';
import { useTenant } from '@/contexts/TenantContext';
import { GitBranch, Lock, Globe, Search, RefreshCw, ExternalLink, Tags, X } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { cn } from '@/lib/utils';
import { matchesRepoQuery } from './repoFilter';

const BULK = '__bulk__';

export default function GitHubConnectionPage() {
  const {
    installations,
    repos,
    loading,
    error,
    syncStatuses,
    syncing,
    planUsage,
    refresh,
    connectRepo,
    disconnectRepo,
    syncRepo,
    removeInstallation,
  } = useGitHubConnection();
  const { tenant } = useTenant();
  const isAdmin = ['admin', 'owner'].includes(tenant?.role ?? '');

  // Each list carries its own filter (name or description) under its heading:
  // the Available list scrolls in its own box, far from anything placed above
  // the Connected list.
  const [connectedQuery, setConnectedQuery] = useState('');
  const [availableQuery, setAvailableQuery] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Business taxonomy (enterprise-project-organization B1): chips for
  // everyone, tagging/projects for admins. Dormant until values exist.
  const [dimensions, setDimensions] = useState<TaxonomyDimension[]>([]);
  const [tagFilters, setTagFilters] = useState<Record<string, string>>({});
  const [editingTags, setEditingTags] = useState<string | null>(null); // fullName | BULK
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [localTags, setLocalTags] = useState<Record<string, Record<string, string>>>({});

  const loadTaxonomy = useCallback(async () => {
    try {
      setDimensions(await projectsApi.getTaxonomy());
    } catch {
      // Taxonomy is optional chrome — the page works without it.
    }
  }, []);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);

  const tagsOf = useCallback(
    (repo: Repo) => localTags[repo.full_name] ?? repo.tags ?? {},
    [localTags],
  );

  const hasTaxonomyValues = dimensions.some((d) => d.values.some((v) => !v.archivedAt));

  const connectedRepos = repos.filter((r) => r.connected);
  const availableRepos = repos.filter((r) => !r.connected);

  const applyFilters = useCallback(
    <T extends Repo>(items: T[]) => {
      let out = items;
      for (const [dim, value] of Object.entries(tagFilters)) {
        if (value) out = out.filter((r) => tagsOf(r)[dim] === value);
      }
      return out;
    },
    [tagFilters, tagsOf],
  );

  const filteredConnected = applyFilters(connectedRepos).filter((r) =>
    matchesRepoQuery(r, connectedQuery),
  );
  const filteredAvailable = applyFilters(availableRepos).filter((r) =>
    matchesRepoQuery(r, availableQuery),
  );

  async function handleToggle(owner: string, repo: string, connected: boolean) {
    const fullName = `${owner}/${repo}`;
    setToggling(fullName);
    try {
      if (connected) {
        await disconnectRepo(owner, repo);
      } else {
        await connectRepo(owner, repo);
      }
    } catch (e) {
      // The at-limit intercept usually catches this first; the backend
      // guard still wins any race (stale usage, concurrent connect).
      if (e instanceof RepoLimitError) setShowLimitModal(true);
      else throw e;
    } finally {
      setToggling(null);
    }
  }

  async function saveTags(repo: Repo, tags: Record<string, string>) {
    await githubApi.setRepoTags(repo.owner, repo.repo, tags);
    setLocalTags((prev) => ({ ...prev, [repo.full_name]: tags }));
    setEditingTags(null);
  }

  async function saveBulkTags(chosenDims: Record<string, string>) {
    const targets = repos.filter((r) => selected.has(r.full_name));
    for (const repo of targets) {
      const merged = { ...tagsOf(repo), ...chosenDims };
      await githubApi.setRepoTags(repo.owner, repo.repo, merged);
      setLocalTags((prev) => ({ ...prev, [repo.full_name]: merged }));
    }
    setSelected(new Set());
    setEditingTags(null);
  }

  const toggleSelected = useCallback((fullName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fullName)) next.delete(fullName);
      else next.add(fullName);
      return next;
    });
  }, []);

  const rowProps = {
    toggling,
    onToggle: handleToggle,
    atLimit: planUsage?.at_limit ?? false,
    onLimitBlocked: () => setShowLimitModal(true),
    dimensions,
    isAdmin,
    selected,
    onSelect: toggleSelected,
    editingTags,
    onEditTags: setEditingTags,
    tagsOf,
    onSaveTags: saveTags,
    onValueCreated: loadTaxonomy,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <GitBranch className="w-5 h-5 text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            GitHub Integration
          </h2>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="p-2 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
        </button>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
        Connect your GitHub account and manage repositories to index.
      </p>

      {error && (
        <div className="mb-6 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* GitHub App installations */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            GitHub App
          </h2>
          <InstallGitHubApp />
        </div>
        <ConnectionStatus
          installations={installations}
          onRemove={removeInstallation}
        />
      </section>

      {/* Repositories */}
      {installations.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Repositories
            </h2>
            <PlanBadge usage={planUsage} />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Manage which repositories SystemDox indexes for documentation.
          </p>

          {loading ? (
            <LoadingState size="md" label="Loading repositories..." />
          ) : (
            <>
              {/* Tag filters (both lists); each list has its own text filter */}
              {repos.length > 0 && hasTaxonomyValues && (
                <div className="mb-6 flex flex-wrap items-center gap-2">
                  {dimensions
                    .filter((d) => !d.archivedAt && d.values.some((v) => !v.archivedAt))
                    .map((dim) => (
                      <select
                        key={dim.dimKey}
                        value={tagFilters[dim.dimKey] ?? ''}
                        onChange={(e) =>
                          setTagFilters((prev) => ({ ...prev, [dim.dimKey]: e.target.value }))
                        }
                        className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-600 dark:text-slate-300"
                      >
                        <option value="">{dim.label}: any</option>
                        {dim.values
                          .filter((v) => !v.archivedAt)
                          .map((v) => (
                            <option key={v.valueSlug} value={v.valueSlug}>
                              {v.label}
                            </option>
                          ))}
                      </select>
                    ))}
                  {Object.values(tagFilters).some(Boolean) && (
                    <button
                      type="button"
                      onClick={() => setTagFilters({})}
                      className="text-xs text-slate-400 hover:text-slate-600 underline"
                    >
                      clear
                    </button>
                  )}
                </div>
              )}

              {/* Bulk tagging bar */}
              {isAdmin && selected.size > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-3 p-2 rounded-md bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-700 dark:text-blue-300">
                    <span>
                      {selected.size} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditingTags(BULK)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <Tags className="w-3 h-3" /> Tag selected
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelected(new Set())}
                      className="text-xs underline"
                    >
                      clear selection
                    </button>
                  </div>
                  {editingTags === BULK && (
                    <RepoTagEditor
                      repoLabel={`${selected.size} selected repositories`}
                      dimensions={dimensions}
                      initial={{}}
                      bulk
                      onApply={saveBulkTags}
                      onCancel={() => setEditingTags(null)}
                      onValueCreated={loadTaxonomy}
                    />
                  )}
                </div>
              )}

              {/* Connected repos */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Connected ({countLabel(filteredConnected.length, connectedRepos.length)})
                </h3>
                {connectedRepos.length > 0 && (
                  <RepoListFilter list="connected" value={connectedQuery} onChange={setConnectedQuery} />
                )}
                {filteredConnected.length > 0 ? (
                  <div className="space-y-1">
                    {filteredConnected.map((repo) => (
                      <RepoRow
                        key={repo.full_name}
                        repo={repo}
                        syncStatus={syncStatuses[repo.full_name]}
                        syncing={syncing === repo.full_name}
                        onSync={syncRepo}
                        {...rowProps}
                      />
                    ))}
                  </div>
                ) : connectedRepos.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                    No repositories connected yet. Connect one below to start indexing.
                  </p>
                ) : (
                  <p className="text-sm text-slate-500 py-2 text-center">
                    {noMatchLabel('connected', connectedQuery)}
                  </p>
                )}
              </div>

              {/* Available repos */}
              {availableRepos.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    Available ({countLabel(filteredAvailable.length, availableRepos.length)})
                  </h3>
                  <RepoListFilter list="available" value={availableQuery} onChange={setAvailableQuery} />
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    {filteredAvailable.map((repo) => (
                      <RepoRow key={repo.full_name} repo={repo} {...rowProps} />
                    ))}
                    {filteredAvailable.length === 0 && (
                      <p className="text-sm text-slate-500 py-2 text-center">
                        {noMatchLabel('available', availableQuery)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

        </section>
      )}

      {showLimitModal && (
        <RepoLimitModal
          limit={planUsage?.repo_limit ?? 1}
          plan={planUsage?.plan ?? 'free'}
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </div>
  );
}

function SyncStatusBadge({ status }: { status: SyncStatus }) {
  const label =
    status.status === 'completed'
      ? `${status.file_count} files`
      : status.status === 'syncing'
        ? 'Syncing...'
        : status.status === 'failed'
          ? 'Sync failed'
          : 'Not synced';

  const color =
    status.status === 'completed'
      ? 'text-green-600 dark:text-green-400'
      : status.status === 'syncing'
        ? 'text-blue-600 dark:text-blue-400'
        : status.status === 'failed'
          ? 'text-red-600 dark:text-red-400'
          : 'text-slate-400';

  return (
    <span className={cn('text-xs', color)}>
      {label}
      {status.status === 'completed' && status.last_sync_at && (
        <span className="text-slate-400 ml-1">
          &middot; {new Date(status.last_sync_at).toLocaleDateString()}
        </span>
      )}
    </span>
  );
}

/** "3" when nothing is filtered out, "1 of 3" when something is. */
function countLabel(shown: number, total: number): string {
  return shown === total ? String(total) : `${shown} of ${total}`;
}

function noMatchLabel(list: 'connected' | 'available', query: string): string {
  const q = query.trim();
  return q ? `No ${list} repository matches “${q}”` : 'No matches';
}

/**
 * The text filter under a list's heading: name or description, every word
 * must match (see `matchesRepoQuery`); its X button or Escape clears it.
 */
function RepoListFilter({
  list,
  value,
  onChange,
}: {
  list: 'connected' | 'available';
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative mb-2">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type="search"
        aria-label={`Filter ${list} repositories`}
        placeholder={`Filter ${list} repositories by name or description`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onChange('');
        }}
        className="w-full pl-10 pr-9 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={`Clear the ${list} filter`}
          title="Clear the filter"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function RepoRow({
  repo,
  toggling,
  onToggle,
  atLimit,
  onLimitBlocked,
  syncStatus,
  syncing,
  onSync,
  dimensions,
  isAdmin,
  selected,
  onSelect,
  editingTags,
  onEditTags,
  tagsOf,
  onSaveTags,
  onValueCreated,
}: {
  repo: Repo;
  toggling: string | null;
  onToggle: (owner: string, repo: string, connected: boolean) => void;
  atLimit: boolean;
  onLimitBlocked: () => void;
  syncStatus?: SyncStatus;
  syncing?: boolean;
  onSync?: (owner: string, repo: string) => void;
  dimensions: TaxonomyDimension[];
  isAdmin: boolean;
  selected: Set<string>;
  onSelect: (fullName: string) => void;
  editingTags: string | null;
  onEditTags: (fullName: string | null) => void;
  tagsOf: (repo: Repo) => Record<string, string>;
  onSaveTags: (repo: Repo, tags: Record<string, string>) => Promise<void>;
  onValueCreated: () => Promise<void>;
}) {
  const tags = tagsOf(repo);
  const canTag = isAdmin && dimensions.length > 0;
  // At the plan limit, Connect stays clickable but opens the limit
  // modal instead — a disabled button would swallow both the tooltip
  // and the click, hiding the upgrade path.
  const limitBlocked = atLimit && !repo.connected;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 min-w-0">
          {canTag && (
            <input
              type="checkbox"
              checked={selected.has(repo.full_name)}
              onChange={() => onSelect(repo.full_name)}
              className="shrink-0"
              title="Select for bulk tagging"
            />
          )}
          <GitBranch className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <a
                href={`https://github.com/${repo.full_name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-slate-900 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 truncate"
              >
                {repo.full_name}
              </a>
              <ExternalLink className="w-3 h-3 text-slate-300 shrink-0" />
              {repo.private ? (
                <Lock className="w-3 h-3 text-slate-400 shrink-0" />
              ) : (
                <Globe className="w-3 h-3 text-slate-400 shrink-0" />
              )}
            </div>
            {repo.description && (
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                {repo.description}
              </p>
            )}
            <div className="flex items-center gap-2 mt-0.5">
              <TagChips tags={tags} dimensions={dimensions} />
              {repo.connected && syncStatus && <SyncStatusBadge status={syncStatus} />}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {canTag && (
            <button
              type="button"
              onClick={() => onEditTags(editingTags === repo.full_name ? null : repo.full_name)}
              className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Edit tags"
            >
              <Tags className="w-3.5 h-3.5" />
            </button>
          )}
          {repo.connected && onSync && (
            <button
              type="button"
              onClick={() => onSync(repo.owner, repo.repo)}
              disabled={syncing}
              className={cn(
                'p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors',
                syncing && 'opacity-50',
              )}
              title="Sync repository"
            >
              <RefreshCw className={cn('w-3.5 h-3.5', syncing && 'animate-spin')} />
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              limitBlocked ? onLimitBlocked() : onToggle(repo.owner, repo.repo, repo.connected)
            }
            disabled={toggling === repo.full_name}
            title={
              limitBlocked
                ? 'Free plan limit reached — disconnect a repo or book a demo'
                : undefined
            }
            className={cn(
              'px-3 py-1 rounded text-xs font-medium transition-colors',
              repo.connected
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100'
                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100',
              (toggling === repo.full_name || limitBlocked) && 'opacity-50',
            )}
          >
            {toggling === repo.full_name ? '...' : repo.connected ? 'Disconnect' : 'Connect'}
          </button>
        </div>
      </div>
      {editingTags === repo.full_name && (
        <div className="px-3 pb-3">
          <RepoTagEditor
            repoLabel={repo.full_name}
            dimensions={dimensions}
            initial={tags}
            onApply={(chosen) => onSaveTags(repo, chosen)}
            onCancel={() => onEditTags(null)}
            onValueCreated={onValueCreated}
          />
        </div>
      )}
    </div>
  );
}
